import { useResizeObserver } from '@orn/shared-web/hooks/useResizeObserver';
import { ArrowDownIcon, ArrowUpIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { toCurrency } from '../../../utils/formatting';

import {
  resolveValueRowLayout,
  sameValueRowMetrics,
  VALUE_CLASS_NAME,
  VALUE_STEP_CLASS_NAMES,
  VALUE_STEPS,
  type IValueRowMetrics,
} from './valueStep';

import type { IValueChange } from '../../../utils/account/balanceHistory';

function changeToneClassName(amount: number): string {
  if (amount === 0) {
    return 'text-tertiary';
  }
  return amount > 0 ? 'text-success-primary' : 'text-error-primary';
}

interface IValueWithChangeProps {
  formattedValue: string;
  change: IValueChange;
  changeLabel?: string;
}

/**
 * A value with its change on the same row. Measures the value at each type step through invisible
 * rulers and takes the largest step that fits beside the change; when none fits, the change goes
 * under the value. Mount it only with both halves, so its observers attach to live elements.
 */
const ValueWithChange = ({ formattedValue, change, changeLabel }: IValueWithChangeProps) => {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const changeRef = React.useRef<HTMLDivElement>(null);
  const largestRulerRef = React.useRef<HTMLSpanElement | null>(null);
  const rulerRefs = React.useRef<Array<HTMLSpanElement | null>>([]);
  const [metrics, setMetrics] = React.useState<IValueRowMetrics | null>(null);

  const measure = React.useCallback(() => {
    const row = rowRef.current;
    if (!row) {
      return;
    }
    const next: IValueRowMetrics = {
      rowWidth: row.offsetWidth,
      changeWidth: changeRef.current?.offsetWidth ?? 0,
      valueWidths: VALUE_STEPS.map((_, index) => rulerRefs.current[index]?.offsetWidth ?? 0),
    };
    setMetrics((previous) => (previous && sameValueRowMetrics(previous, next) ? previous : next));
  }, []);

  // Layout effect so the first paint already has its step; observers cover resizes and font swaps.
  React.useLayoutEffect(() => {
    measure();
  }, [measure, formattedValue, change.amount, change.percent, changeLabel]);
  useResizeObserver({ ref: rowRef, onResize: measure });
  useResizeObserver({ ref: changeRef, onResize: measure });
  useResizeObserver({ ref: largestRulerRef, onResize: measure });

  const layout = resolveValueRowLayout(metrics);

  return (
    <div
      ref={rowRef}
      className={cx('relative flex items-center justify-between gap-x-4 gap-y-1', layout.isWrapped && 'flex-wrap')}
    >
      {VALUE_STEPS.map((step, index) => (
        <span
          key={step}
          ref={(element) => {
            rulerRefs.current[index] = element;
            if (index === 0) {
              largestRulerRef.current = element;
            }
          }}
          aria-hidden="true"
          data-value-ruler={step}
          className={cx(VALUE_CLASS_NAME, VALUE_STEP_CLASS_NAMES[step], 'pointer-events-none invisible absolute')}
        >
          {formattedValue}
        </span>
      ))}

      <div
        data-testid="current-value"
        className={cx(VALUE_CLASS_NAME, VALUE_STEP_CLASS_NAMES[layout.step], 'shrink-0')}
      >
        {formattedValue}
      </div>

      <div ref={changeRef} data-testid="current-value-change" className="ml-auto flex shrink-0 flex-col items-end">
        <div className={cx('flex items-center gap-0.5 text-sm font-medium', changeToneClassName(change.amount))}>
          {change.amount > 0 && <ArrowUpIcon className="size-4 shrink-0" />}
          {change.amount < 0 && <ArrowDownIcon className="size-4 shrink-0" />}
          <span className="whitespace-nowrap">
            {toCurrency(Math.abs(change.amount))}
            {change.percent !== null && ` (${Math.abs(change.percent).toFixed(2)}%)`}
          </span>
        </div>
        {changeLabel && <div className="text-tertiary whitespace-nowrap text-xs font-medium">{changeLabel}</div>}
      </div>
    </div>
  );
};

export default ValueWithChange;
